import React, { useEffect } from 'react';
import { Field, reduxForm } from 'redux-form';
import { Col, Container, Row } from 'reactstrap';
import renderField from '../../../shared/components/custom/Field';
import renderDropZoneField from '../../../shared/components/form/DropZoneMS';
import renderSwitchField from '../../../shared/components/custom/Switch';
import renderDateTimePickerField from '../../../shared/components/form/DateTimePicker';
import { getImageHash } from '../../../util/functions';
import { partial } from '../../../util/translation/translation';
import validateChatForm from './validateChatForm';

const s = partial('shared');
const p = partial('chat');

// Seconds between one user's messages; '' is off
const SLOW_MODE = ['', '10', '30', '60', '300'];

const ChatForm = ({ chat, handleSubmit, initialize, change }) => {
  useEffect(() => {
    if (chat) {
      initialize({
        name: chat.name,
        description: chat.description,
        isActive: !!chat.isActive,
        opensAt: chat.opensAt ? new Date(chat.opensAt) : null,
        closesAt: chat.closesAt ? new Date(chat.closesAt) : null,
        slowModeSeconds: chat.slowModeSeconds
          ? String(chat.slowModeSeconds)
          : '',
        image: chat.imageUrl
          ? {
              name: 'chat image',
              preview: chat.imageUrl,
              croppedImage: chat.imageUrl,
              hash: getImageHash(chat.imageUrl),
            }
          : undefined,
      });
    } else {
      initialize({
        isActive: true,
      });
    }
  }, [chat, initialize]);

  return (
    <form className="form" onSubmit={handleSubmit}>
      <div
        style={{
          backgroundColor: '#F4F4F6',
          borderRadius: 4,
          borderColor: '#D7DAE0',
          borderStyle: 'solid',
          borderWidth: 1,
          marginBottom: 10,
          width: '100%',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottomWidth: 1,
            borderBottomStyle: 'dashed',
            borderColor: '#D7DAE0',
            padding: '10px 10px 10px 10px',
          }}
        >
          <div
            style={{
              fontSize: 16,
              lineHeight: '24px',
              color: 'black',
              fontWeight: 'bold',
            }}
          >
            {s('image')}
          </div>
        </div>
        <div style={{ padding: '10px' }}>
          <Container>
            <Row>
              <Col md={12}>
                <div className="form__form-group">
                  <Field
                    saveBase64Cropped
                    name="image"
                    maxSize={3}
                    component={renderDropZoneField}
                    displayCrop
                    crop="BANNER_CROP"
                  />
                  <span className="form-header">
                    <strong>{s('note')}:</strong> {s('recommendedImageSize')}{' '}
                    16:9 (1035 x 582 px)
                  </span>
                </div>
              </Col>
            </Row>
          </Container>
        </div>
      </div>
      <div
        style={{
          backgroundColor: '#F4F4F6',
          borderRadius: 4,
          borderColor: '#D7DAE0',
          borderStyle: 'solid',
          borderWidth: 1,
          marginTop: 10,
          marginBottom: 10,
          width: '100%',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottomWidth: 1,
            borderBottomStyle: 'dashed',
            borderColor: '#D7DAE0',
            padding: '10px 10px 10px 10px',
          }}
        >
          <div
            style={{
              fontSize: 16,
              lineHeight: '24px',
              color: 'black',
              fontWeight: 'bold',
            }}
          >
            {s('details')}
          </div>
        </div>
        <div style={{ padding: '10px' }}>
          <Container>
            <Row>
              <Col md={6}>
                <div className="form__form-group">
                  <span className="form-header">{s('name')}*</span>
                  <Field
                    name="name"
                    component={renderField}
                    placeholder={s('name')}
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className="form__form-group">
                  <span className="form-header">{s('description')}</span>
                  <Field
                    name="description"
                    component={renderField}
                    placeholder={s('description')}
                  />
                </div>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <div className="form__form-group flex-Vcenter">
                  <span className="form-header" />
                  <Field
                    name="isActive"
                    component={renderSwitchField}
                    label={[`${s('active')}`, `${s('inActive')}`]}
                    defaultChecked
                    className="colored-click"
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className="form__form-group">
                  <span className="form-header">{p('slowMode')}</span>
                  <Field
                    name="slowModeSeconds"
                    component="select"
                    className="form-control"
                  >
                    {SLOW_MODE.map((seconds) => (
                      <option key={seconds || 'off'} value={seconds}>
                        {!seconds && p('slowModeOff')}
                        {seconds &&
                          Number(seconds) < 60 &&
                          p('slowModeSeconds', { count: seconds })}
                        {seconds &&
                          Number(seconds) >= 60 &&
                          p('slowModeMinutes', { count: Number(seconds) / 60 })}
                      </option>
                    ))}
                  </Field>
                </div>
              </Col>
            </Row>
            <Row>
              {['opensAt', 'closesAt'].map((name) => (
                <Col md={6} key={name}>
                  <div className="form__form-group">
                    <span className="form-header">
                      {p(name)}{' '}
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 ml-2 mb-0"
                        onClick={() => change(name, null)}
                      >
                        {p('removeDate')}
                      </button>
                    </span>
                    <Field
                      name={name}
                      component={renderDateTimePickerField}
                      timeFormat="p"
                      dateFormat="Pp"
                      timeIntervals={15}
                    />
                  </div>
                </Col>
              ))}
              <Col md={12}>
                <small className="text-muted">{p('scheduleInfo')}</small>
              </Col>
            </Row>
          </Container>
        </div>
      </div>
    </form>
  );
};

const reduxF = reduxForm({
  form: 'chat_form',
  validate: validateChatForm,
});

export default reduxF(ChatForm);
